/**
 * Phase 9 Session A (gaps XC-1, 082-2) — AdminHomePage is now a real landing
 * hub instead of a placeholder. Renders a grid of admin tools with optional
 * pending-count badges sourced from `useAdminCounts()`.
 *
 * The `data-testid="admin-home"` attribute is preserved so Gate 088c.A still
 * passes.
 *
 * Phase 9 Session C (gap 088c-2) — the `__LAZY_FORCE_GRAPH_MOUNT__`
 * tree-shaker hack was removed now that `LazyForceGraph` is used by real
 * routes (CoverageDashboardPage, FanOutGraphPage). Vite emits the
 * `react-force-graph-2d` chunk naturally from those dynamic imports.
 */
import { useEffect, useState } from 'react';
import { Icon } from '../components/ui/Icon.js';
import { useAdminCounts } from '../hooks/useAdminCounts.js';
import { useTenantBudget } from '../hooks/useTenantBudget.js';
import { apiGet } from '../lib/api.js';
import { RowList, Row, RowFact } from '../components/ui/Row.js';
import { Chip } from '../components/ui/Chip.js';

interface TileConfig {
  title: string;
  to: string;
  /** Boxicon name, not a component. Spec 230. */
  icon: string;
  description: string;
  badge?: number | null;
  disabled?: boolean;
  disabledReason?: string;
  testId: string;
  // Phase 9 Session E.1 — optional role-boundary hint shown next to the title.
  roleHint?: string;
}

export function AdminHomePage() {
  const { candidatesPending, suggestionsPending, error } = useAdminCounts();

  const tiles: TileConfig[] = [
    {
      title: 'Rule library',
      to: '/admin/rules',
      icon: 'bx-book-open',
      description: 'Browse, activate, and deprecate evaluation rules.',
      testId: 'admin-tile-rule-library',
    },
    {
      title: 'Rule candidates',
      to: '/admin/rules/candidates',
      icon: 'bx-star',
      description: 'Review new rules distilled from community posts.',
      badge: candidatesPending,
      testId: 'admin-tile-rule-candidates',
    },
    {
      title: 'Rule suggestions',
      to: '/admin/rules/suggestions',
      icon: 'bx-git-pull-request',
      description: 'Approve patches to existing rules from community posts.',
      badge: suggestionsPending,
      testId: 'admin-tile-rule-suggestions',
    },
    {
      title: 'Rule sources',
      to: '/admin/rule-sources',
      icon: 'bx-rss',
      description: 'Manage the Medium RSS feeds the ingestion worker polls.',
      testId: 'admin-tile-rule-sources',
    },
    {
      title: 'Feature flags',
      to: '/admin/feature-flags',
      icon: 'bx-toggle-right',
      description: 'Toggle background workers and client-facing features.',
      testId: 'admin-tile-feature-flags',
    },
    {
      title: 'Page evaluations',
      to: '/admin/evaluations',
      icon: 'bx-search-alt',
      description: 'Review rule-level evaluation results across crawled pages.',
      testId: 'admin-tile-page-evaluations',
    },
    {
      // Phase 9 Session E.1 — cross-link from /admin into the consultant
      // sitemap-coverage flow. The route lives outside ProtectedRoute so the
      // tile is accessible to admins; the small "Consultant" hint marks the
      // role boundary.
      title: 'Sitemap coverage',
      to: '/coverage',
      icon: 'bx-layer',
      description: "Triage a brand's sitemap and run a coverage analysis.",
      testId: 'admin-tile-sitemap-coverage',
      roleHint: 'Consultant',
    },
  ];

  return (
    <div data-testid="admin-home" className="hub-page--table">
      <header className="flex items-center gap-3">
        <div className="rounded-full bg-violet-100 p-3">
          <Icon name="bx-search-alt" className="text-2xl text-violet-700" />
        </div>
        <div>
          <h1 className="hub-page__title">Observatory</h1>
          <p className="text-sm text-slate-600">
            Tools for consultants — restricted to users with the{' '}
            <code className="hub-code">rule_admin</code> or{' '}
            <code className="hub-code">orbit:admin</code> role.
          </p>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Couldn't load pending counts: {error}
        </div>
      )}

      {/* Spec 235 §3.2 — lg:grid-cols-3 here is a tray of identical tiles,
          not columns that mean something: decorative, so it converts to
          rows. AdminTile and BudgetTile share one RowList — Row's optional
          `to` is exactly what lets a navigable tile (AdminTile) and an
          info-only one (BudgetTile) sit in the same list. */}
      <RowList>
        {tiles.map((tile) => (
          <AdminTile key={tile.title} tile={tile} />
        ))}
        <BudgetTile />
      </RowList>

      <WorkersPanel />
    </div>
  );
}

/**
 * Background-worker liveness panel. Surfaces, at a glance, whether each
 * scheduled background worker is still ticking. A worker that has silently
 * stopped firing is sorted to the top with a "Stalled" badge so a stalled
 * brand-collection pipeline is visible before a brand sits empty for days.
 *
 * Data: GET /api/v1/health/subsystems exposes a `workers[]` array plus a
 * `workers_stale_count`. We reuse the same `apiGet` helper and 30s polling
 * cadence as the admin tiles so the panel self-refreshes.
 *
 * Badge states (see `deriveBadge`):
 *   - stale === true                      → "Stalled" (rose) — no tick in 3× cadence
 *   - !stale && last_status === 'error'   → "Erroring" (amber) — last tick failed
 *   - !stale && last_status === 'skipped' → "Idle · flag off" (slate) — the worker's
 *                                           cron fired and responded but did no work
 *                                           because its feature flag is off (a
 *                                           flag-gated no-op). NOT "Live" — it isn't
 *                                           working — and NOT "Erroring" — nothing failed.
 *   - otherwise                           → "Live" (emerald)
 *
 * Precedence is stale → error → skipped → live: a stale worker still reads
 * "Stalled" even if its last status was 'skipped' (silence wins over the
 * benign idle signal), while a fresh skipped worker reads the calm idle state.
 */
interface WorkerLiveness {
  name: string;
  cadence_seconds: number;
  last_tick_at: string | null;
  age_seconds: number | null;
  last_status: 'ok' | 'error' | 'skipped' | null;
  stale: boolean;
}

interface SubsystemsHealth {
  workers: WorkerLiveness[];
  workers_stale_count: number;
}

const WORKERS_POLL_INTERVAL_MS = 30_000;

type BadgeState = 'stale' | 'error' | 'skipped' | 'live';

function deriveBadge(worker: WorkerLiveness): BadgeState {
  if (worker.stale) return 'stale';
  if (worker.last_status === 'error') return 'error';
  if (worker.last_status === 'skipped') return 'skipped';
  return 'live';
}

/**
 * Human-friendly relative age from the worker's `last_tick_at`. We prefer the
 * server-computed `age_seconds` (already a clock-skew-free delta against the
 * server's NOW()) and fall back to "never" when the worker has no recorded
 * tick. Renders the coarsest sensible unit: seconds → minutes → hours → days.
 */
function formatAge(worker: WorkerLiveness): string {
  if (worker.last_tick_at === null || worker.age_seconds === null) {
    return 'never';
  }
  const secs = Math.max(0, Math.floor(worker.age_seconds));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function WorkersPanel() {
  const [data, setData] = useState<SubsystemsHealth | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const next = await apiGet<SubsystemsHealth>('/api/v1/health/subsystems');
        if (cancelled) return;
        setData(next);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    const id = window.setInterval(() => {
      void load();
    }, WORKERS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Stalled workers float to the top so problems are immediately visible;
  // erroring next, then healthy "Live". A "skipped" (flag-off idle) worker is
  // benign — it sorts last (tier 3) so it never crowds out real problems.
  // Stable alphabetical tiebreak within a tier.
  const tier = (w: WorkerLiveness): number => {
    const state = deriveBadge(w);
    switch (state) {
      case 'stale':
        return 0;
      case 'error':
        return 1;
      case 'live':
        return 2;
      case 'skipped':
        return 3;
    }
  };
  const sortedWorkers = data?.workers
    ? [...data.workers].sort(
        (a, b) => tier(a) - tier(b) || a.name.localeCompare(b.name),
      )
    : [];

  const staleCount = data?.workers_stale_count ?? 0;

  return (
    <section
      data-testid="worker-liveness-panel"
      className="hub-plate mt-8"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-violet-50 p-2 text-violet-700">
            <Icon name="bx-pulse" className="text-lg" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Workers</h2>
            <p className="text-sm text-slate-600">
              Background jobs that keep brands fresh, and whether they're still
              running.
            </p>
          </div>
        </div>

        {!loading && !error && data && (
          staleCount > 0 ? (
            <Chip tone="fail" data-testid="worker-summary-stale" className="shrink-0">
              {staleCount} stalled
            </Chip>
          ) : (
            <Chip tone="ok" data-testid="worker-summary-live" className="shrink-0">
              All workers live
            </Chip>
          )
        )}
      </div>

      {loading && (
        <div className="space-y-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-10 animate-pulse rounded-md bg-slate-100"
            />
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Couldn't load worker status: {error}
        </div>
      )}

      {!loading && !error && data && sortedWorkers.length === 0 && (
        <p className="text-sm text-slate-500">No workers reported.</p>
      )}

      {!loading && !error && sortedWorkers.length > 0 && (
        <RowList>
          {sortedWorkers.map((worker) => (
            <WorkerRow key={worker.name} worker={worker} />
          ))}
        </RowList>
      )}
    </section>
  );
}

// A worker has no destination and no action of its own — pure status
// display, so it is a disclosure row: no `to`, no lead/actions slot needed.
function WorkerRow({ worker }: { worker: WorkerLiveness }) {
  const state = deriveBadge(worker);
  const age = formatAge(worker);

  return (
    <Row
      data-testid={`worker-row-${worker.name}`}
      title={worker.name}
      top={<WorkerBadge state={state} />}
    >
      <RowFact icon="bx-time-five">{age}</RowFact>
    </Row>
  );
}

function WorkerBadge({ state }: { state: BadgeState }) {
  if (state === 'stale') {
    return (
      <Chip
        tone="fail"
        data-testid="worker-status-stale"
        title="No tick in over 3× the expected interval — this worker may not be running."
      >
        Stalled
      </Chip>
    );
  }
  if (state === 'error') {
    return (
      <Chip tone="warn" data-testid="worker-status-error" title="This worker's last run returned an error.">
        Erroring
      </Chip>
    );
  }
  if (state === 'skipped') {
    return (
      <Chip
        data-testid="worker-status-skipped"
        title="This worker's feature flag is off — it's responding but doing no work."
      >
        Idle · flag off
      </Chip>
    );
  }
  return (
    <Chip tone="ok" data-testid="worker-status-live">
      Live
    </Chip>
  );
}

/**
 * Spec backend-followups — info-only tile (not a Link) that surfaces the
 * active tenant's monthly spend vs cap. Hidden until a tenant id resolves so
 * we never render meaningless "$0.00 of $0" copy.
 *
 * Visual states:
 *   - default:  slate border
 *   - >= 80%:   amber border + "{percentage}% used" pill
 *   - >= 100%:  rose border + "Cap exceeded" pill
 */
function BudgetTile() {
  const { budget } = useTenantBudget();

  // No tenant yet (or first fetch in flight) — render nothing rather than a
  // skeleton, per task guidance. Future fetches replace this with the real
  // tile.
  if (!budget) return null;

  const { monthly_cost_cap_usd: cap, monthly_spend_usd: spent } = budget;
  const pct = budget.percentage_used;
  const exceeded = pct >= 100;
  const warning = pct >= 80 && !exceeded;

  const description = `$${spent.toFixed(2)} of $${cap.toFixed(0)} used this month`;

  return (
    // Info-only — no detail page, no action — so this is a disclosure row
    // too, sharing the RowList with the navigable AdminTiles above it. The
    // old border colour (slate/amber/rose) is dropped without information
    // loss: the "Cap exceeded"/"{pct}% used" pill already carries the same
    // status as text, so the border was a redundant second signal, not the
    // only one (contrast DiagnosisCard's severity accent, which was).
    <Row
      data-testid="admin-tile-budget"
      lead={
        <div className="rounded-md bg-violet-50 p-2 text-violet-700">
          <Icon name="bx-wallet" className="text-xl" />
        </div>
      }
      title="Budget"
      top={
        <>
          {exceeded && (
            <Chip tone="fail" data-testid="admin-tile-budget-pill">
              Cap exceeded
            </Chip>
          )}
          {warning && (
            <Chip tone="warn" data-testid="admin-tile-budget-pill">
              {Math.round(pct)}% used
            </Chip>
          )}
        </>
      }
    >
      <p className="text-sm text-slate-600">{description}</p>
      {/* Spec 161 (D-2) — always-visible usage bar + percentage, not just the
          >=80% pill, so low usage still shows how full the budget is. */}
      <div className="mt-2">
        <div
          className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full rounded-full ${exceeded ? 'bg-rose-400' : warning ? 'bg-amber-400' : 'bg-violet-400'}`}
            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
          />
        </div>
        <p data-testid="budget-percentage" className="mt-1 text-xs text-slate-500">
          {pct < 0.5 && pct > 0 ? '<1' : Math.round(pct)}% used
        </p>
      </div>
      {/* Spec 161 (D-1) — per-stream breakdown so the blended total is
          auditable. Untouched: its own 2-col <dl> is inner layout, and gate
          161.A.2 pins the budget-stream-breakdown testid to this exact
          component. */}
      <BudgetStreamBreakdown byStream={budget.by_stream} />
    </Row>
  );
}

/** Spec 161 — compact per-stream cost breakdown under the Budget tile. */
const BUDGET_STREAM_LABELS: Record<string, string> = {
  queries: 'Queries',
  page_evaluations: 'Page evals',
  fan_out: 'Fan-out',
  llms_txt: 'llms.txt',
};

function BudgetStreamBreakdown({ byStream }: { byStream?: Record<string, number> }) {
  if (!byStream) return null;
  const entries = Object.entries(byStream);
  if (entries.length === 0) return null;
  return (
    <dl data-testid="budget-stream-breakdown" className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
      {entries.map(([key, amount]) => (
        <div key={key} className="flex items-center justify-between text-xs text-slate-500">
          <dt>{BUDGET_STREAM_LABELS[key] ?? key}</dt>
          <dd className="tabular-nums text-[var(--hub-color-ink)]">${amount.toFixed(2)}</dd>
        </div>
      ))}
    </dl>
  );
}

// A disabled tile has no destination — Row's optional `to` covers that
// directly (disclosure shape) rather than the old two-branch div-vs-Link
// split with two copies of the same inner markup.
function AdminTile({ tile }: { tile: TileConfig }) {
  const {
    title,
    to,
    icon,
    description,
    badge,
    disabled,
    disabledReason,
    testId,
    roleHint,
  } = tile;

  const badgeVisible = typeof badge === 'number' && badge > 0;
  // While the hook is still loading, tiles with a badge source still render
  // immediately but show a subtle skeleton where the badge will appear.
  const badgePending = badge === null || badge === undefined;
  const hasBadgeSlot = 'badge' in tile;

  return (
    <Row
      to={disabled ? undefined : to}
      data-testid={testId}
      aria-disabled={disabled ? 'true' : undefined}
      className={disabled ? 'opacity-70' : ''}
      title={title}
      lead={
        <div
          className={`rounded-md p-2 ${
            disabled ? 'bg-slate-100 text-slate-400' : 'bg-violet-50 text-violet-700'
          }`}
        >
          <Icon name={icon} className="text-xl" />
        </div>
      }
      top={
        <>
          {/* The native tooltip explaining WHY lives on this span, not on
              the row: Row's own `title` prop is already the row's heading
              content, so there is nowhere else to hang an HTML title
              attribute without colliding with it. */}
          {disabled && (
            <span className="text-xs font-normal text-slate-400" title={disabledReason}>
              (coming soon)
            </span>
          )}
          {roleHint && !disabled && (
            <Chip data-testid={`${testId}-role-hint`} className="text-xs capitalize">
              {roleHint}
            </Chip>
          )}
          {hasBadgeSlot && !disabled && badgePending && (
            <span
              aria-hidden
              className="h-5 w-8 animate-pulse rounded-full bg-slate-100"
            />
          )}
          {badgeVisible && (
            <span
              data-testid={`${testId}-badge`}
              className="rounded-full bg-rose-500 px-2 py-0.5 text-xs font-medium text-white"
            >
              {badge}
            </span>
          )}
        </>
      }
    >
      <p className={`text-sm ${disabled ? 'text-slate-400' : 'text-slate-600'}`}>
        {description}
      </p>
    </Row>
  );
}
